
import React, { useEffect, useRef, useState } from 'react';
import { Puzzle, User } from '../types';
import { 
  Trophy, ArrowRight, Heart, Swords, Sparkles, BookOpen, Volume2, Eye, Star, 
  Share2, FileSpreadsheet, Copy, Check, Download, X, RefreshCw 
} from 'lucide-react';
import { SUPPORT_LINK } from '../constants';
import ImagePreviewModal from './ImagePreviewModal';
import { ParentalGate } from './Layouts';

// Canvas drawing utility helpers
function wrapTextCentered(
  ctx: CanvasRenderingContext2D, 
  text: string, 
  x: number, 
  y: number, 
  maxWidth: number, 
  lineHeight: number
): number {
  const words = text.split(' ');
  let line = '';
  let currentY = y;
  const lines: string[] = [];

  for (let n = 0; n < words.length; n++) {
    const testLine = line + words[n] + ' ';
    const metrics = ctx.measureText(testLine);
    const testWidth = metrics.width;
    if (testWidth > maxWidth && n > 0) {
      lines.push(line.trim());
      line = words[n] + ' ';
    } else {
      line = testLine;
    }
  }
  lines.push(line.trim());

  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i], x, currentY);
    currentY += lineHeight;
  }
  return currentY;
}

function getWrappedLines(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string[] {
  const words = text.split(' ');
  let line = '';
  const lines: string[] = [];

  for (let n = 0; n < words.length; n++) {
    const testLine = line + words[n] + ' ';
    const metrics = ctx.measureText(testLine);
    const testWidth = metrics.width;
    if (testWidth > maxWidth && n > 0) {
      lines.push(line.trim());
      line = words[n] + ' ';
    } else {
      line = testLine;
    }
  }
  lines.push(line.trim());
  return lines;
}

function drawRoundedRect(
  ctx: CanvasRenderingContext2D, 
  x: number, 
  y: number, 
  width: number, 
  height: number, 
  radius: number
): void {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

interface VictoryScreenProps {
  puzzle: Puzzle;
  onNext: () => void;
  isChallenge?: boolean;
  challengerName?: string;
  onRevenge?: () => void;
  stats?: { 
    moves: number; 
    time: number; 
    faithScore: number;
    formattedDate?: string;
    excelFullTSV?: string;
  };
  onOpenStory: () => void;
  onSupport: () => void;
  user?: User | null;
  triggerOnlineAction?: (featureName: string, action: () => void) => void;
}

const VictoryScreen: React.FC<VictoryScreenProps> = ({ 
  puzzle, onNext, isChallenge, challengerName, onRevenge, stats, onOpenStory, onSupport, user, triggerOnlineAction
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [finishDate] = useState(() => new Date());
  const [copiedSocial, setCopiedSocial] = useState(false);
  const [copiedExcel, setCopiedExcel] = useState(false);
  const [downloadedCSV, setDownloadedCSV] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isDeviceSharePopupOpen, setIsDeviceSharePopupOpen] = useState(false);
  const [isVisualShareOpen, setIsVisualShareOpen] = useState(false);
  const [visualShareImage, setVisualShareImage] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isActionPopupOpen, setIsActionPopupOpen] = useState(false);
  const [showTournamentQuery, setShowTournamentQuery] = useState(false);
  const [gateAction, setGateAction] = useState<(() => void) | null>(null);

  const handleGateProtectedAction = (action: () => void) => {
    setGateAction(() => action);
  };

  // Completion Date Formatter: "time, day, month, and year format"
  const formattedDate = stats?.formattedDate || (() => {
    const time = finishDate.toTimeString().split(' ')[0]; // "14:23:45"
    const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const dayName = weekdays[finishDate.getDay()];
    const dayNum = finishDate.getDate();
    const monthName = months[finishDate.getMonth()];
    const yearNum = finishDate.getFullYear();

    return `${time}, ${dayName}, ${dayNum} ${monthName} ${yearNum}`;
  })();

  const activeUserId = user?.email || user?.id || user?.username || "Guest_Player";
  const displayFaithScore = stats ? (stats.faithScore || Math.max(0, Math.round(((Math.max(0, 360 - stats.time) + Math.max(0, 180 - stats.moves) + 10) / 10) * 10) / 10)) : 0;
  const roundedFaithScore = Math.round(displayFaithScore * 10) / 10;

  const socialShareText = `✝️ Bible Puzzle Quiz App ✝️
🏆 Faith score recorded: ${roundedFaithScore} points!

🧩 Puzzle: "${puzzle.title}"
👤 Player ID: ${activeUserId}
⏰ Completed: ${formattedDate}
📊 Moves: ${stats?.moves || 0}
⏱️ Duration: ${stats?.time || 0} seconds
✨ Faith Score: ${roundedFaithScore}${isChallenge ? "\n\n(Lower moves and duration and higher Faith Scores are better)" : ""}`;

  // Excel TSV line (perfect for clipboard copying and direct paste in Excel)
  const excelHeaderTSV = "Player ID\tCompletion Timestamp\tPuzzle Title\tMoves\tPlay Duration (Seconds)\tFaith Score";
  const excelRowTSV = `${activeUserId}\t${formattedDate}\t${puzzle.title}\t${stats?.moves || 0}\t${stats?.time || 0}\t${roundedFaithScore}`;
  const excelFullTSV = stats?.excelFullTSV || `${excelHeaderTSV}\n${excelRowTSV}`;

  const generateScoreCardText = (scoreDetails: {
    playerEmail: string;
    completionTime: string;
    puzzleTitle: string;
    moves: number;
    playDuration: number;
    faithScore: number;
  }) => {
    return [
      scoreDetails.playerEmail,
      `"${scoreDetails.completionTime}"`,
      `"${scoreDetails.puzzleTitle}"`,
      scoreDetails.moves,
      scoreDetails.playDuration,
      scoreDetails.faithScore
    ].join(",");
  };

  const getBPQuestSubmissionUrl = (scoreCardText: string) => {
    const bpQuestBaseUrl = "https://www.bpquest.name.ng"; 
    const encodedScore = encodeURIComponent(scoreCardText);
    return `${bpQuestBaseUrl}/?score=${encodedScore}`;
  };

  const handleDirectSubmit = () => {
    const scoreText = generateScoreCardText({
      playerEmail: user?.email || "guest@gmail.com",
      completionTime: formattedDate,
      puzzleTitle: puzzle.title,
      moves: stats?.moves || 0,
      playDuration: stats?.time || 0,
      faithScore: roundedFaithScore
    });

    const redirectUrl = getBPQuestSubmissionUrl(scoreText);
    window.location.assign(redirectUrl);
  };

  // 1080x1920 high-fidelity celestial victory poster generator
  useEffect(() => {
    if (isVisualShareOpen) {
      setIsGenerating(true);
      
      const logoImg = new Image();
      logoImg.src = '/assets/icons/launcher-icon.png';
      
      const drawCanvas = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = 1080;
          canvas.height = 1920;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            setIsGenerating(false);
            return;
          }

          // 2. Expanded Middle Band (#d6e7ff) covering the whole top area now
          ctx.fillStyle = '#d6e7ff';
          ctx.fillRect(0, 0, 1080, 1460);

          // Bottom Separator line (#dc2626)
          ctx.fillStyle = '#dc2626';
          ctx.fillRect(0, 1460, 1080, 15);

          // 3. Bottom Band (#0047ab) with statistics and sign-off
          ctx.fillStyle = '#0047ab';
          ctx.fillRect(0, 1475, 1080, 445);

          // 1. Render puzzle title as the card header (super bold)
          ctx.textAlign = 'center';
          ctx.fillStyle = '#0047ab';
          ctx.font = '900 64px "Inter", "system-ui", "-apple-system", sans-serif';
          ctx.fillText(`"${puzzle.title}"`, 540, 120);

          // 2. Centered Verse & Prayer Block within the remaining light blue area
          let verseBlockHeight = 0;
          let verseLines: string[] = [];
          
          // Determine font sizes dynamically so they share the exact same size & font, maximizing legibility
          let sharedFontSize = 54;
          let sharedLineHeight = 74;

          const totalLength = (puzzle.winVerse ? puzzle.winVerse.length : 0) + (puzzle.prayer ? puzzle.prayer.length : 0);
          if (totalLength > 400) {
            sharedFontSize = 42;
            sharedLineHeight = 58;
          } else if (totalLength > 300) {
            sharedFontSize = 48;
            sharedLineHeight = 66;
          }

          if (puzzle.winVerse) {
            ctx.font = `900 ${sharedFontSize}px "Inter", "system-ui", "-apple-system", sans-serif`;
            const verseStr = `"${puzzle.winVerse}"`;
            verseLines = getWrappedLines(ctx, verseStr, 920);
            
            // Height = Title (42px) + Gap to Verse (45px) + wrapped text height
            verseBlockHeight = 42 + 45 + (verseLines.length * sharedLineHeight);
          }

          let gapBlockHeight = 0;
          if (puzzle.winVerse && puzzle.prayer) {
            gapBlockHeight = 90; // clean vertical gap between blocks
          }

          let prayerBlockHeight = 0;
          let prayerLines: string[] = [];

          if (puzzle.prayer) {
            ctx.font = `900 ${sharedFontSize}px "Inter", "system-ui", "-apple-system", sans-serif`;
            const prayerStr = puzzle.prayer;
            prayerLines = getWrappedLines(ctx, prayerStr, 920);

            // Height = Title (42px) + Gap to Prayer (45px) + wrapped text height
            prayerBlockHeight = 42 + 45 + (prayerLines.length * sharedLineHeight);
          }

          const totalContentsHeight = verseBlockHeight + gapBlockHeight + prayerBlockHeight;
          const contentAreaStartY = 200;
          const contentAreaHeight = 1230; // area from 200 to 1430
          
          let currentMiddleY = contentAreaStartY + (contentAreaHeight - totalContentsHeight) / 2;

          // Render Scripture Verse
          if (puzzle.winVerse) {
            // Label / Title: bold but not as bold as card header
            ctx.fillStyle = '#0047ab'; 
            ctx.font = '900 42px "Inter", "system-ui", "-apple-system", sans-serif';
            ctx.fillText('📖  VICTORY VERSE  📖', 540, currentMiddleY + 34);
            currentMiddleY += 34 + 45;

            // Verse Text
            ctx.fillStyle = '#0047ab'; 
            ctx.font = `900 ${sharedFontSize}px "Inter", "system-ui", "-apple-system", sans-serif`;
            verseLines.forEach((line) => {
              ctx.fillText(line, 540, currentMiddleY + sharedFontSize - 10);
              currentMiddleY += sharedLineHeight;
            });
          }

          // Gap between blocks
          if (puzzle.winVerse && puzzle.prayer) {
            currentMiddleY += 90;
          }

          // Render Prayer text
          if (puzzle.prayer) {
            // Label / Title: bold but not as bold as card header
            ctx.fillStyle = '#0047ab';
            ctx.font = '900 42px "Inter", "system-ui", "-apple-system", sans-serif';
            ctx.fillText('🙏  A PRAYER FOR YOU  🙏', 540, currentMiddleY + 34);
            currentMiddleY += 34 + 45;

            // Prayer Text (same font and size as victory text)
            ctx.fillStyle = '#0047ab';
            ctx.font = `900 ${sharedFontSize}px "Inter", "system-ui", "-apple-system", sans-serif`;
            prayerLines.forEach((line) => {
              ctx.fillText(line, 540, currentMiddleY + sharedFontSize - 10);
              currentMiddleY += sharedLineHeight;
            });
          }

          // 3. Third Band Content: Statistics of play and sign-off
          const statsY = 1570;
          const showFaithScore = !!isChallenge;

          const movesVal = stats?.moves !== undefined ? String(stats.moves) : '0';
          const timeVal = stats?.time !== undefined ? `${stats.time}s` : '0s';
          const faithScoreVal = String(roundedFaithScore);

          let statCards: { label: string, value: string, xPos: number, cWidth: number }[] = [];

          if (showFaithScore) {
            // If Faith Duel, show exactly 3 stats centered beautifully
            const cWidth = 250;
            statCards = [
              { label: 'MOVES', value: movesVal, xPos: 540 - 290, cWidth },
              { label: 'DURATION', value: timeVal, xPos: 540, cWidth },
              { label: 'FAITH SCORE', value: faithScoreVal, xPos: 540 + 290, cWidth }
            ];
          } else {
            // For a regular play, show ONLY the two stats requested to avoid clogging the layout
            const cWidth = 320;
            statCards = [
              { label: 'MOVES', value: movesVal, xPos: 330, cWidth },
              { label: 'DURATION', value: timeVal, xPos: 750, cWidth }
            ];
          }

          // Render explanation text: "Lower moves and duration values are better."
          ctx.textAlign = 'center';
          ctx.fillStyle = '#d6e7ff';
          ctx.font = 'bold italic 26px "Inter", "system-ui", "-apple-system", sans-serif';
          ctx.fillText('Lower moves and duration values are better.', 540, 1525);

          statCards.forEach((cItem) => {
            const cardXStart = cItem.xPos - cItem.cWidth / 2;
            
            // Outer red borders with transparent-overlay background
            ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
            ctx.strokeStyle = '#dc2626';
            ctx.lineWidth = 4;
            drawRoundedRect(ctx, cardXStart, statsY, cItem.cWidth, 150, 24);
            ctx.fill();
            ctx.stroke();

            // Render value (bold white)
            ctx.textAlign = 'center';
            ctx.fillStyle = '#ffffff';
            ctx.font = 'bold 54px "Inter", "system-ui", "-apple-system", sans-serif';
            ctx.fillText(cItem.value, cItem.xPos, statsY + 64);

            // Render label text (light celestial cream color)
            ctx.fillStyle = '#d6e7ff';
            ctx.font = 'bold 22px "Inter", "system-ui", "-apple-system", sans-serif';
            ctx.fillText(cItem.label, cItem.xPos, statsY + 116);
          });

          // 6. Watermark branding row with white sign off lettering and launcher icon
          const watermarkY = 1815;
          ctx.fillStyle = '#ffffff'; // White for sign-off lettering
          ctx.font = 'bold 32px "Inter", "system-ui", "-apple-system", sans-serif';
          
          const labelText = '✝  BIBLE PUZZLE QUIZ  ✝';
          const textMetrics = ctx.measureText(labelText);
          const textWidth = textMetrics.width;
          
          const logoSize = 64;
          const gapBetween = 20;
          const totalBrandWidth = logoSize + gapBetween + textWidth;
          
          const brandStartX = 540 - totalBrandWidth / 2;
          
          // Draw logo icon if loaded successfully
          try {
            ctx.drawImage(logoImg, brandStartX, watermarkY - 46, logoSize, logoSize);
          } catch (e) {
            console.error("Failed to draw logo onto canvas", e);
          }
          
          // Draw centered text aligned left from the text start point
          ctx.textAlign = 'left';
          ctx.fillText(labelText, brandStartX + logoSize + gapBetween, watermarkY - 2);

          // Parse Canvas to base64
          const dataUrl = canvas.toDataURL('image/png');
          setVisualShareImage(dataUrl);
          setIsGenerating(false);
        } catch (error) {
          console.error("Canvas rendering failed", error);
          setIsGenerating(false);
        }
      };

      const timer = setTimeout(() => {
        if (logoImg.complete) {
          drawCanvas();
        } else {
          logoImg.onload = drawCanvas;
          logoImg.onerror = drawCanvas; // solid fallback if image fails, to avoid soft-lock
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isVisualShareOpen, puzzle.title, puzzle.winVerse, puzzle.prayer, stats?.moves, stats?.time, roundedFaithScore]);

  const downloadImageFile = () => {
    if (!visualShareImage) return;
    const link = document.createElement('a');
    link.href = visualShareImage;
    link.download = `BiblePuzzleQuiz_${puzzle.title.replace(/\s+/g, '_')}_Victory.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const shareImageFile = async () => {
    if (!visualShareImage) return;
    try {
      const res = await fetch(visualShareImage);
      const blob = await res.blob();
      const file = new File([blob], `BiblePuzzleQuiz_${puzzle.title.replace(/\s+/g, '_')}_Victory.png`, { type: 'image/png' });
      
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `Bible Puzzle Quiz - ${puzzle.title} Victory`,
          text: `Hello, I did the "${puzzle.title}" puzzle in Bible Puzzle Quiz today. I hope this victory message inspires you.`
        });
      } else {
        await navigator.share({
          title: `Bible Puzzle Quiz - ${puzzle.title} Victory`,
          text: `Hello, I did the "${puzzle.title}" puzzle in Bible Puzzle Quiz today. I hope this victory message inspires you.\n\nVerse:\n"${puzzle.winVerse}"\n\nPrayer:\n${puzzle.prayer}`
        });
      }
    } catch (err) {
      console.log('Native file share failed:', err);
    }
  };

  const handleDeviceShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Bible Puzzle Quiz - ${puzzle.title} Score`,
          text: socialShareText,
        });
      } catch (err) {
        console.log("Error launching native share sheet:", err);
      }
    } else {
      alert("Native sharing is not supported on this browser. Please use the Copy Code option below!");
    }
  };

  const copyToClipboard = (text: string, type: 'social' | 'excel') => {
    const performCopy = () => {
      if (type === 'social') {
        setCopiedSocial(true);
        setTimeout(() => setCopiedSocial(false), 2000);
      } else {
        setCopiedExcel(true);
        setTimeout(() => setCopiedExcel(false), 2000);
      }
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text)
        .then(performCopy)
        .catch(() => fallbackCopy(text));
    } else {
      fallbackCopy(text);
    }

    function fallbackCopy(textString: string) {
      const textArea = document.createElement("textarea");
      textArea.value = textString;
      textArea.style.position = "fixed";
      textArea.style.top = "0";
      textArea.style.left = "0";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          performCopy();
        } else {
          alert('Could not copy automatically. Please copy the text manually.');
        }
      } catch (err) {
        alert('Could not copy automatically. Please copy the text manually.');
      }
      document.body.removeChild(textArea);
    }
  };

  const handleDownloadCSV = () => {
    try {
      const headers = "Player ID,Completion Timestamp,Puzzle Title,Moves,Play Duration (Seconds),Faith Score\n";
      const escapeCSV = (str: string) => `"${str.replace(/"/g, '""')}"`;
      
      const row = `${escapeCSV(activeUserId)},` +
                  `${escapeCSV(formattedDate)},` +
                  `${escapeCSV(puzzle.title)},` +
                  `${stats?.moves || 0},` +
                  `${stats?.time || 0},` +
                  `${roundedFaithScore}\n`;
                  
      const blob = new Blob([headers + row], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `Faith_Duel_Score_${puzzle.title.replace(/\s+/g, '_')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setDownloadedCSV(true);
      setTimeout(() => setDownloadedCSV(false), 2000);
    } catch (err) {
      console.error("CSV Download error:", err);
      alert("Error generating CSV.");
    }
  };

  useEffect(() => {
    try {
      const stored = localStorage.getItem('bp_quiz_score_archive');
      let archive: Array<{ puzzleTitle: string; formattedDate: string; code: string; timestamp: number }> = [];
      if (stored) {
        archive = JSON.parse(stored);
      }
      
      const isDuplicate = archive.some(
        item => item.puzzleTitle === puzzle.title && item.formattedDate === formattedDate
      );
      
      if (!isDuplicate) {
        const newRecord = {
          puzzleTitle: puzzle.title,
          formattedDate,
          code: excelFullTSV,
          timestamp: Date.now()
        };
        archive = [newRecord, ...archive];
        archive = archive.slice(0, 3);
        localStorage.setItem('bp_quiz_score_archive', JSON.stringify(archive));
      }
    } catch (e) {
      console.error("Failed to archive score", e);
    }
  }, [puzzle.title, formattedDate, excelFullTSV]);

  useEffect(() => {
    const audio = new Audio(puzzle.successSoundUrl);
    audio.play().catch(e => console.warn("Autoplay audio failed:", e));
    audioRef.current = audio;

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [puzzle.successSoundUrl]);

  return (
    <div className="flex-1 flex flex-col items-center justify-start bg-slate-50 p-4 md:p-8 min-h-screen text-center animate-in fade-in duration-700 relative overflow-y-auto pt-safe px-safe">
      <ParentalGate 
        isOpen={!!gateAction} 
        onClose={() => setGateAction(null)} 
        onSuccess={() => {
          if (gateAction) gateAction();
          setGateAction(null);
        }}
        title="Adult Verification"
        message="To continue out-of-app, please ask a parent or guardian to solve this sum:"
      />
      {/* Decorative Particles */}
      <div className="fixed inset-0 pointer-events-none opacity-20 z-0 text-rose-400">
        {[...Array(15)].map((_, i) => (
          <Sparkles 
            key={i}
            className="absolute animate-pulse"
            style={{
              width: Math.random() * 20 + 10 + 'px',
              height: Math.random() * 20 + 10 + 'px',
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
              animationDelay: Math.random() * 5 + 's',
              animationDuration: Math.random() * 3 + 2 + 's'
            }}
          />
        ))}
      </div>

      <div className="relative w-full max-w-2xl mt-8 mb-8 z-10 flex flex-col items-center">
        {/* Trophy Section */}
        <div className="mb-6 relative z-30">
          <div className={`${isChallenge ? 'bg-rose-600' : 'bg-yellow-400'} p-8 rounded-full shadow-2xl border-8 border-white inline-block animate-bounce-slow`}>
            {isChallenge ? <Swords className="w-16 h-16 text-white" /> : <Trophy className="w-16 h-16 text-white" />}
          </div>
        </div>

        {/* Unified Victory Card */}
        <div className="w-full bg-white rounded-[3rem] overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.12)] border border-slate-100 flex flex-col">
           <div className="p-8 md:p-12 bg-white">
             <h1 className="text-4xl md:text-5xl font-cinzel font-bold text-gray-900 mb-2">
               {isChallenge ? 'DUEL OVER!' : 'Hallelujah!'}
             </h1>
             <p className="text-xl text-yellow-700 font-bold mb-8 italic font-serif">
               {isChallenge ? `You completed ${challengerName}'s challenge!` : `You have mastered "${puzzle.title}"`}
             </p>
             
             <div className="w-24 h-1 bg-yellow-400/20 mx-auto mb-10 rounded-full" />
             
             {/* Win Verse and Prayer */}
             <div className="space-y-10 text-left bg-slate-50 p-8 rounded-[2rem] border border-slate-100">
                {puzzle.winVerse && (
                  <div className="relative">
                    <Sparkles className="absolute -top-4 -left-4 w-6 h-6 text-yellow-400 opacity-40" />
                    <p className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-3">Victory Verse</p>
                    <p className="text-lg md:text-xl text-slate-800 font-serif italic leading-relaxed">
                      "{puzzle.winVerse}"
                    </p>
                  </div>
                )}
                
                {puzzle.prayer && (
                  <div>
                    <p className="text-xs font-bold text-rose-600 uppercase tracking-widest mb-3">A Prayer for You</p>
                    <p className="text-base text-slate-600 font-medium leading-relaxed">
                      {puzzle.prayer}
                    </p>
                  </div>
                )}

                {!puzzle.winVerse && !puzzle.prayer && (
                  <p className="text-gray-500 text-center italic py-4">
                    {puzzle.description}. May this story inspire your walk with God.
                  </p>
                )}
             </div>

             {stats && (
               <div className="flex flex-col items-center mt-10">
                 <div className="flex justify-center gap-4 w-full">
                   <div className="bg-white px-4 py-3 rounded-2xl border border-slate-100 shadow-sm min-w-[90px] flex-1 max-w-[124px]">
                     <p className="text-xl font-bold text-gray-900">{stats.moves}</p>
                     <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Moves</p>
                   </div>
                   <div className="bg-white px-4 py-3 rounded-2xl border border-slate-100 shadow-sm min-w-[90px] flex-1 max-w-[124px]">
                     <p className="text-xl font-bold text-gray-900">{stats.time}s</p>
                     <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Time</p>
                   </div>
                   {isChallenge && (
                    <div className="bg-blue-50 px-4 py-3 rounded-2xl border border-blue-100 shadow-sm min-w-[90px] flex-1 max-w-[124px]">
                     <p className="text-xl font-bold text-blue-700">{roundedFaithScore}</p>
                     <p className="text-[10px] uppercase font-bold text-blue-400 tracking-wider">Faith Score</p>
                   </div>
                  )}
                 </div>



                 {/* Share & Export Score Section */}
                 {false && (
                  <div className="w-full mt-6 pt-6 border-t border-slate-100 flex flex-col items-center">
                                     {!isShareOpen ? (
                                       <button
                                         onClick={() => setIsShareOpen(true)}
                                         className="bg-blue-50 hover:bg-blue-100/80 text-blue-700 font-bold text-xs py-3.5 px-6 rounded-2xl transition shadow-sm active:scale-95 flex items-center gap-2"
                                       >
                                         <Share2 className="w-4 h-4" />
                                         Share & Export my Score
                                       </button>
                                     ) : (
                                       <div className="w-full space-y-4 animate-in fade-in slide-in-from-top-4 duration-300">
                                         <div className="flex justify-between items-center w-full">
                                           <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Score Share & Export Panel</span>
                                           <button
                                             onClick={() => setIsShareOpen(false)}
                                             className="text-xs text-slate-400 hover:text-slate-600 font-bold uppercase tracking-wider transition-colors"
                                           >
                                             Hide
                                           </button>
                                         </div>
                  
                                         {/* Preview Box */}
                                         <div className="w-full border border-slate-100 bg-slate-50/70 rounded-[1.5rem] p-4 text-left font-mono text-xs text-slate-600 space-y-2 mt-2 leading-relaxed">
                                           <div className="flex justify-between border-b border-slate-200/50 pb-2">
                                             <span className="text-gray-400 font-sans font-bold uppercase text-[9px] tracking-wider">Metric</span>
                                             <span className="text-gray-400 font-sans font-bold uppercase text-[9px] tracking-wider">Data Value</span>
                                           </div>
                                           <div className="flex justify-between py-0.5">
                                             <span className="text-gray-400 font-sans">Player ID:</span> 
                                             <span className="font-bold text-gray-800 break-all pl-2 text-right">{activeUserId}</span>
                                           </div>
                                           <div className="flex justify-between py-0.5">
                                             <span className="text-gray-400 font-sans">Timestamp:</span> 
                                             <span className="font-bold text-gray-800 text-right">{formattedDate}</span>
                                           </div>
                                           <div className="flex justify-between py-0.5">
                                             <span className="text-gray-400 font-sans">Puzzle:</span> 
                                             <span className="font-bold text-gray-800 text-right">"{puzzle.title}"</span>
                                           </div>
                                           <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-200/50 text-center">
                                             <div className="bg-white/80 p-2 rounded-xl border border-slate-100">
                                               <span className="text-gray-400 block text-[9px] font-sans uppercase">Moves</span>
                                               <span className="font-bold text-gray-800">{stats?.moves || 0}</span>
                                             </div>
                                             <div className="bg-white/80 p-2 rounded-xl border border-slate-100">
                                               <span className="text-gray-400 block text-[9px] font-sans uppercase">Duration</span>
                                               <span className="font-bold text-gray-800">{stats?.time || 0}s</span>
                                             </div>
                                             <div className="bg-blue-50/50 p-2 rounded-xl border border-blue-100">
                                               <span className="text-blue-400 block text-[9px] font-sans uppercase">Faith Score</span>
                                               <span className="font-bold text-blue-700">{roundedFaithScore}</span>
                                             </div>
                                           </div>
                                          </div>

                                          {/* Action Tools Row */}
                                         <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full pt-1">
                                           {/* Share Trigger button */}
                                           <button
                                             onClick={() => setIsDeviceSharePopupOpen(true)}
                                             className="flex items-center justify-center gap-2 py-4 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl text-xs font-bold transition shadow-md active:scale-95 cursor-pointer"
                                             title="Open sharing app selector and copy code options"
                                           >
                                             <Share2 className="w-4 h-4" />
                                             Share
                                           </button>
                  
                                           {/* Excel row copy */}
                                           <button
                                             onClick={() => copyToClipboard(excelFullTSV, 'excel')}
                                             className="flex items-center justify-center gap-2 py-4 px-4 bg-slate-800 hover:bg-slate-900 text-white rounded-2xl text-xs font-bold transition shadow-md active:scale-95 cursor-pointer"
                                             title="Copy tournament data row formatted for BPQuest companion website"
                                           >
                                             {copiedExcel ? <Check className="w-4 h-4 text-emerald-300 animate-pulse" /> : <FileSpreadsheet className="w-4 h-4" />}
                                             {copiedExcel ? "Copied for BPQuest!" : "Excel for BPQuest"}
                                           </button>
                                         </div>
                                         
                                         <p className="text-[10px] font-sans font-medium text-slate-400 tracking-normal text-center mt-2 leading-relaxed max-w-md mx-auto">
                                           📊 <strong>BPQuest Tournament Integration:</strong> Copy spreadsheet data formatted specifically for organizers running tournaments with BPQuest (the online companion application for Bible Puzzle Quiz).
                                         </p>
                                       </div>
                                     )}
                                   </div>
                )}
               </div>
             )}
           </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-4 w-full max-w-2xl z-10 pb-16 pb-safe px-4">
        {isChallenge ? (
          <button 
            onClick={() => setIsActionPopupOpen(true)}
            className="col-span-2 bg-[#fc5603] hover:bg-[#fc5603]/90 text-white py-5 rounded-[2.5rem] font-bold text-xl transition-all shadow-2xl flex items-center justify-center gap-3 active:scale-95 mb-2 cursor-pointer"
          >
            <span>⚡</span>
            <span>Action</span>
            <ArrowRight className="w-6 h-6" />
          </button>
        ) : (
          <>
            <button 
              onClick={onNext}
              className="col-span-2 bg-blue-700 hover:bg-blue-800 text-white py-5 rounded-[2.5rem] font-bold text-xl transition-all shadow-2xl flex items-center justify-center gap-3 active:scale-95 mb-2"
            >
              <Sparkles className="w-6 h-6" />
              Explore More Puzzles
              <ArrowRight className="w-6 h-6" />
            </button>

            {!isChallenge && (
              <button 
                onClick={() => setIsVisualShareOpen(true)}
                className="col-span-2 bg-emerald-600 hover:bg-emerald-700 text-white py-5 rounded-[2.5rem] font-bold text-xl transition-all shadow-2xl flex items-center justify-center gap-3 active:scale-95 mb-2"
              >
                <Share2 className="w-6 h-6" />
                Share Victory Screen
              </button>
            )}

            <button 
              onClick={onOpenStory}
              className="bg-white hover:bg-blue-50 text-blue-700 py-6 rounded-[2.5rem] font-bold transition-all shadow-lg border border-blue-100 flex flex-col items-center justify-center gap-1 active:scale-95"
            >
              <BookOpen className="w-7 h-7" />
              <span className="text-[10px] font-bold uppercase tracking-widest mt-1">Read/Listen</span>
            </button>

            <button 
              onClick={() => setIsPreviewOpen(true)}
              className="bg-white hover:bg-blue-50 text-blue-700 py-6 rounded-[2.5rem] font-bold transition-all shadow-lg border border-blue-100 flex flex-col items-center justify-center gap-1 active:scale-95"
            >
              <Eye className="w-7 h-7" />
              <span className="text-[10px] font-bold uppercase tracking-widest mt-1">See Artwork</span>
            </button>

            <a 
              href={SUPPORT_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="col-span-2 bg-red-600 hover:bg-red-700 text-white py-4 rounded-[2.5rem] font-bold text-sm transition-all shadow-xl flex items-center justify-center gap-2 active:scale-95 mt-2"
            >
              <Star className="w-5 h-5 fill-white" />
              <span>Rate Us</span>
            </a>
          </>
        )}
      </div>

      <ImagePreviewModal 
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        imageUrl={puzzle.imageUrl}
        title={puzzle.title}
      />

      {/* Action Popup Modal for Challenge */}
      {isActionPopupOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Overlay background */}
          <div 
            className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm transition-opacity"
            onClick={() => setIsActionPopupOpen(false)}
          />
          
          {/* Main Dialog card */}
          <div className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-[0_30px_60px_rgba(0,0,0,0.25)] border border-slate-100 p-6 md:p-8 overflow-y-auto max-h-[90vh] z-10 animate-in fade-in zoom-in-95 duration-200 text-left">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="font-cinzel font-bold text-xl text-slate-950 flex items-center gap-2">
                  <Swords className="w-5 h-5 text-blue-600" />
                  Faith Duel Actions
                </h3>
                <p className="text-xs text-slate-400 font-medium mt-1">
                  Choose an action to proceed with your results.
                </p>
              </div>
              <button 
                onClick={() => setIsActionPopupOpen(false)}
                className="p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-all active:scale-95"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {showTournamentQuery ? (
              <div className="bg-orange-50 border border-orange-100 rounded-3xl p-5 space-y-4 animate-in fade-in zoom-in-95 duration-200">
                <div className="flex items-center gap-2.5 text-orange-900 font-semibold text-sm">
                  <span className="text-xl">⚠️</span>
                  <span>Tournament Participation Check</span>
                </div>
                <p className="text-xs text-orange-800 leading-relaxed font-semibold">
                  Are you currently participating in a tournament at the BPQuest website?
                </p>
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <button
                    onClick={() => {
                      setShowTournamentQuery(false);
                      setIsActionPopupOpen(false);
                      handleGateProtectedAction(() => handleDirectSubmit());
                    }}
                    className="py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition active:scale-95 cursor-pointer text-center font-sans"
                  >
                    Yes, submit
                  </button>
                  <button
                    onClick={() => {
                      setShowTournamentQuery(false);
                    }}
                    className="py-3 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl text-xs font-bold transition active:scale-95 cursor-pointer text-center"
                  >
                    No, close
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* 1. Direct Submit to BPQuest */}
                <button
                  onClick={() => {
                    const submitAction = () => setShowTournamentQuery(true);
                    if (triggerOnlineAction) {
                      triggerOnlineAction('Direct Submissions to BPQuest', submitAction);
                    } else {
                      submitAction();
                    }
                  }}
                  className="w-full flex items-center justify-between p-4 bg-orange-50 hover:bg-orange-100/80 border border-orange-100 rounded-2xl transition active:scale-[0.99] group text-left cursor-pointer"
                >
                  <div className="pr-3">
                    <span className="text-xs font-bold text-orange-800 uppercase tracking-wider block flex items-center gap-1.5">
                      <span>⚡</span> Direct Submit to BPQuest
                    </span>
                    <span className="text-[11px] text-orange-700 mt-0.5 block leading-normal">
                      Submit your score block directly to our tournament system at bpquest.name.ng.
                    </span>
                  </div>
                  <ArrowRight className="w-5 h-5 text-orange-600 group-hover:translate-x-1 transition-transform shrink-0" />
                </button>

                {/* 2. Share & Export my Score Box */}
                <div className="border border-slate-100 bg-slate-50/70 rounded-2xl p-4 space-y-3">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 block">Score share & export</span>
                  
                  {/* Share button */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setIsDeviceSharePopupOpen(true);
                      }}
                      className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition shadow-sm active:scale-95 cursor-pointer"
                    >
                      <Share2 className="w-4 h-4" />
                      <span>Share Score</span>
                    </button>

                    {/* Copy excel */}
                    <button
                      onClick={() => copyToClipboard(excelFullTSV, 'excel')}
                      className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-bold transition shadow-sm active:scale-95 cursor-pointer"
                    >
                      {copiedExcel ? <Check className="w-4 h-4 text-emerald-300" /> : <FileSpreadsheet className="w-4 h-4" />}
                      <span>{copiedExcel ? "Copied!" : "Excel Copy"}</span>
                    </button>
                  </div>
                  
                  <p className="text-[10px] text-slate-400 leading-normal tracking-normal">
                    Copy spread sheet data specifically for organizers running tournaments with BPQuest.
                  </p>
                </div>

                <div className="h-px bg-slate-100 my-2" />

                {/* 3. Explore More Puzzles */}
                <button
                  onClick={() => {
                    setIsActionPopupOpen(false);
                    onNext();
                  }}
                  className="w-full flex items-center gap-3 py-3.5 px-4 bg-blue-700 hover:bg-blue-800 text-white rounded-2xl text-xs font-bold transition active:scale-95 cursor-pointer"
                >
                  <Sparkles className="w-4 h-4 text-yellow-300" />
                  <span>Explore More Puzzles</span>
                </button>

                {/* 4. Revenge Challenge */}
                {onRevenge && (
                  <button
                    onClick={() => {
                      const revengeAction = () => {
                        setIsActionPopupOpen(false);
                        onRevenge();
                      };
                      if (triggerOnlineAction) {
                        triggerOnlineAction('Faith Duels / Custom Challenges', revengeAction);
                      } else {
                        revengeAction();
                      }
                    }}
                    className="w-full flex items-center gap-3 py-3.5 px-4 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-150 rounded-2xl text-xs font-bold transition active:scale-95 cursor-pointer"
                  >
                    <Swords className="w-4 h-4" />
                    <span>Send a Revenge Challenge</span>
                  </button>
                )}

                {/* 5. See Artwork & Read Story */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => {
                      setIsActionPopupOpen(false);
                      onOpenStory();
                    }}
                    className="flex items-center justify-center gap-2 py-3 bg-white hover:bg-slate-50 text-blue-700 border border-slate-200 rounded-2xl text-xs font-bold transition active:scale-95 cursor-pointer"
                  >
                    <BookOpen className="w-4 h-4" />
                    <span>Read Story</span>
                  </button>

                  <button
                    onClick={() => {
                      setIsActionPopupOpen(false);
                      setIsPreviewOpen(true);
                    }}
                    className="flex items-center justify-center gap-2 py-3 bg-white hover:bg-slate-50 text-blue-700 border border-slate-200 rounded-2xl text-xs font-bold transition active:scale-95 cursor-pointer"
                  >
                    <Eye className="w-4 h-4" />
                    <span>See Artwork</span>
                  </button>
                </div>

                {/* 6. Rate Us */}
                <button
                  onClick={() => {
                    const rateAction = () => {
                      handleGateProtectedAction(() => {
                        setIsActionPopupOpen(false);
                        window.open(SUPPORT_LINK, '_blank');
                      });
                    };
                    if (triggerOnlineAction) {
                      triggerOnlineAction('App Support & Rating', rateAction);
                    } else {
                      rateAction();
                    }
                  }}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-red-600 hover:bg-red-700 text-white rounded-2xl text-xs font-bold transition active:scale-95 text-center cursor-pointer font-sans"
                >
                  <Star className="w-4 h-4 fill-white" />
                  <span>Rate Us / App Support</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Share Popup App Selector Modal */}
      {isDeviceSharePopupOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Overlay background */}
          <div 
            className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm transition-opacity"
            onClick={() => setIsDeviceSharePopupOpen(false)}
          />
          
          {/* Main Dialog card */}
          <div className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-[0_30px_60px_rgba(0,0,0,0.25)] border border-slate-100 p-6 md:p-8 overflow-hidden z-10 animate-in fade-in zoom-in-95 duration-200 text-left">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="font-cinzel font-bold text-xl text-slate-950 flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-blue-600" />
                  Share Victory Score
                </h3>
                <p className="text-xs text-slate-400 font-medium mt-1">
                  Choose a service to reveal and verify your score.
                </p>
              </div>
              <button 
                onClick={() => setIsDeviceSharePopupOpen(false)}
                className="p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-all active:scale-95"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Device Native Apps Share button (Web Share API) */}
              {typeof navigator.share !== 'undefined' ? (
                <button
                  onClick={() => {
                    const systemShare = () => handleGateProtectedAction(handleDeviceShare);
                    if (triggerOnlineAction) {
                      triggerOnlineAction('Device System Share', systemShare);
                    } else {
                      systemShare();
                    }
                  }}
                  className="w-full flex items-center justify-between p-4 bg-blue-50 hover:bg-blue-100/80 border border-blue-100 rounded-2xl transition active:scale-[0.99] group text-left"
                >
                  <div className="pr-3">
                    <span className="text-xs font-bold text-blue-800 uppercase tracking-wider block">Share via Device Apps</span>
                    <span className="text-xs text-blue-600/80 mt-0.5 block leading-normal">
                      Send to your messaging tools (WhatsApp, Discord, Slack, SMS) directly using your system's share engine.
                    </span>
                  </div>
                  <ArrowRight className="w-5 h-5 text-blue-600 group-hover:translate-x-1 transition-transform shrink-0" />
                </button>
              ) : (
                <div className="p-3 bg-red-50 rounded-xl border border-red-100 text-[11px] text-red-800 leading-relaxed font-semibold">
                  💡 Native system sharing is currently restricted or unsupported by this browser engine. Use the apps or <strong>Copy Code</strong> option instead!
                </div>
              )}

              {/* Direct Web sharing shortcuts as standard fallbacks */}
              <div className="grid grid-cols-2 gap-3 pt-1">
                <button
                  onClick={() => {
                    const waShareAction = () => handleGateProtectedAction(() => {
                      window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(socialShareText)}`, '_blank');
                    });
                    if (triggerOnlineAction) {
                      triggerOnlineAction('WhatsApp Quick Share', waShareAction);
                    } else {
                      waShareAction();
                    }
                  }}
                  className="flex items-center justify-center gap-2 py-3.5 px-3 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-100 rounded-2xl text-xs font-bold transition active:scale-95 text-center font-sans"
                >
                  WhatsApp Quick Share
                </button>
                <button
                  onClick={() => {
                    const tgShareAction = () => handleGateProtectedAction(() => {
                      window.open(`https://t.me/share/url?url=${encodeURIComponent(socialShareText)}`, '_blank');
                    });
                    if (triggerOnlineAction) {
                      triggerOnlineAction('Telegram Quick Share', tgShareAction);
                    } else {
                      tgShareAction();
                    }
                  }}
                  className="flex items-center justify-center gap-2 py-3.5 px-3 bg-sky-50 hover:bg-sky-100 text-sky-800 border border-sky-100 rounded-2xl text-xs font-bold transition active:scale-95 text-center font-sans"
                >
                  Telegram Quick Share
                </button>
              </div>

              <div className="h-px bg-slate-100 my-4" />

              {/* Secure verification code segment */}
              <div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2">Copy Code</span>
                
                <div className="flex flex-col gap-3 p-4 bg-slate-50 border border-slate-100 rounded-2xl">
                  <p className="text-[11px] text-slate-500 leading-normal font-medium">
                    Copy the complete formatted victory post and verification data. You can save this within document editors like Microsoft Word or WPS Office, and share it with other people later on social media platforms.
                  </p>
                  
                  <button
                    onClick={() => copyToClipboard(socialShareText, 'social')}
                    className="flex items-center justify-center gap-2 py-3.5 px-4 bg-slate-900 hover:bg-slate-850 text-white rounded-xl text-xs font-bold transition shadow-sm active:scale-95 cursor-pointer w-full"
                  >
                    {copiedSocial ? (
                      <>
                        <Check className="w-4.5 h-4.5 text-emerald-300 animate-pulse" />
                        <span>Code Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        <span>Copy Code</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Visual Share Image Card Modal */}
      {isVisualShareOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          {/* Overlay background */}
          <div 
            className="absolute inset-0 bg-slate-950/85 backdrop-blur-md transition-opacity"
            onClick={() => setIsVisualShareOpen(false)}
          />
          
          {/* Main Dialog card */}
          <div className="relative w-full max-w-sm bg-white rounded-[2.5rem] shadow-[0_30px_60px_rgba(0,0,0,0.3)] border border-slate-100 p-6 overflow-hidden z-10 animate-in fade-in zoom-in-95 duration-200 text-center flex flex-col justify-between max-h-[90vh]">
            
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-cinzel font-bold text-lg text-slate-950 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-emerald-600 animate-pulse" />
                Share Victory Card
              </h3>
              <button 
                onClick={() => setIsVisualShareOpen(false)}
                className="p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-all active:scale-95 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Generated Image Preview Frame (9:16 Aspect constrained) */}
            <div className="flex-1 flex items-center justify-center bg-slate-50 border border-slate-100 rounded-3xl p-3 mb-4 overflow-hidden relative min-h-[300px]">
              {isGenerating ? (
                <div className="flex flex-col items-center justify-center">
                  <RefreshCw className="w-8 h-8 text-emerald-600 animate-spin mb-3" />
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Drafting Celestial Card...</span>
                </div>
              ) : visualShareImage ? (
                <img 
                  src={visualShareImage} 
                  alt="Victory Card Visual" 
                  className="aspect-[9/16] max-h-[48vh] w-auto h-auto object-contain rounded-2xl shadow-md border border-slate-200/50"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <span className="text-xs text-rose-500 font-bold">Failed to load preview card. Please try again.</span>
              )}
            </div>

            {/* Actions Panel */}
            <div className="space-y-3.5">
              <div className="grid grid-cols-2 gap-3">
                {/* Download */}
                <button
                  onClick={downloadImageFile}
                  disabled={isGenerating || !visualShareImage}
                  className="flex items-center justify-center gap-2 py-3.5 px-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl text-xs font-bold transition shadow-md active:scale-95 cursor-pointer disabled:opacity-50"
                >
                  <Download className="w-4 h-4" />
                  Download PNG
                </button>

                {/* System share */}
                <button
                  onClick={() => handleGateProtectedAction(shareImageFile)}
                  disabled={isGenerating || !visualShareImage}
                  className="flex items-center justify-center gap-2 py-3.5 px-4 bg-blue-700 hover:bg-blue-800 text-white rounded-2xl text-xs font-bold transition shadow-md active:scale-95 cursor-pointer disabled:opacity-50 font-sans"
                >
                  <Share2 className="w-4 h-4" />
                  Share Image
                </button>
              </div>

              <p className="text-[10px] text-gray-400 font-medium leading-relaxed">
                💡 <strong>Tip:</strong> You can also tap and hold on the image preview to save it directly or copy it on your iOS/Android smartphone.
              </p>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default VictoryScreen;

